import React, { useContext } from 'react';
import find from 'lodash/find';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import partialRight from 'lodash/partialRight';
import set from 'lodash/set';
import styled from 'styled-components';

import FormContext from 'src/presentation/contexts/FormContext';
import RemoveFillIcon from '@ad-scripts/presentation/components/common/icons/fill/remove-fill';
import { Button, Checkbox } from 'src/presentation/components/agent_desktop_components';
import { InputRanges } from 'src/presentation/components/pages/shared/HoursTable/TimeRange';

const EMPTY_STATE = [];
const INITIAL_STATE = { start: '', stop: '' };
const DEFAULT_STATE = { start: '09:00', stop: '17:00' };

type OfficeHoursProps = {
  attr: string;
  label: string;
};

export const SingleDayItem = ({ attr, label }: OfficeHoursProps): JSX.Element => {
  const { errors, resetError, workingState, updateState } = useContext(FormContext);
  const timeRanges = get(workingState, attr, []);
  const closed = !(timeRanges ?? []).length;
  const timeRangesToShow = closed ? [INITIAL_STATE] : timeRanges;

  const onAddClick = () => {
    const newTimeRange = [...timeRanges, DEFAULT_STATE];
    updateState(set(workingState, attr, newTimeRange));
  };

  const resetAllErrorsFromIndexToEnd = (attrToReset, indexToResetFrom) => {
    for (let i = indexToResetFrom; i <= timeRanges.length; i++) {
      resetErrorByAttribute(`${attr}[${i}]`);
    }
  };

  const resetErrorByAttribute = attrToReset => {
    forEach(errors, error => {
      const errorAttr = get(error, 'attr', '');
      if (errorAttr.startsWith(attrToReset)) {
        resetError({ attr: errorAttr, errors });
      }
    });
  };

  const onDeleteClick = ({ attr, index }) => {
    resetAllErrorsFromIndexToEnd(attr, index);
    const updatedArray = [...timeRanges.slice(0, index), ...timeRanges.slice(index + 1)];
    updateState(set({}, attr, updatedArray), { replaceArrayValues: true });
  };

  const toggleClosedState = ({ attr, checked }) => {
    resetErrorByAttribute(attr);
    updateState(set(workingState, attr, checked ? EMPTY_STATE : [DEFAULT_STATE]));
  };

  const renderedTimeRanges = timeRangesToShow.map((range, index) => {
    const rangeAttr = `${attr}[${index}]`;
    const startAttr = `${rangeAttr}.start`;
    const stopAttr = `${rangeAttr}.stop`;

    const onTimeChange = ({ fullAttr, value }) => {
      resetErrorByAttribute(fullAttr);
      updateState(set({}, fullAttr, value));
    };

    const errorStart = find(errors, error => error.attr === `${attr}[${index}].start`);
    const errorStop = find(errors, error => error.attr === `${attr}[${index}].stop`);
    const errorOverlapLabel = getOverlappingRangeErrorLabel(attr, index, errors);

    const addTimeRangeButton =
      index === 0 ? (
        <AddTimeRangeButton
          data-aid={`${attr}[${index}]-addTimeRangeButton`}
          disabled={closed}
          type={Button.Types.TEXT}
          onClick={onAddClick}
        >
          ＋ Add Time Range
        </AddTimeRangeButton>
      ) : null;

    const deleteButton =
      index > 0 ? (
        <StyledTrashIcon data-aid={`${rangeAttr}-deleteButton`} onClick={() => onDeleteClick({ attr, index })} />
      ) : null;

    return (
      <SingleInputRangeContainer key={rangeAttr} data-aid={rangeAttr}>
        <InputRanges
          attr={rangeAttr}
          data-aid={rangeAttr}
          disabled={closed}
          errorBothEndsEmpty={errorStart && errorStop}
          errorOverlap={errorOverlapLabel}
          errorStart={errorStart}
          errorStop={errorStop}
          onTimeChange={partialRight(onTimeChange, range)}
          row={range}
          startAttr={startAttr}
          stopAttr={stopAttr}
        />
        <ActionContainer>
          {addTimeRangeButton}
          {deleteButton}
        </ActionContainer>
      </SingleInputRangeContainer>
    );
  });

  const closedCheckBox = (
    <CheckBoxWrapper data-aid={`${attr}-closed`}>
      <Checkbox
        checked={closed}
        onChange={() => toggleClosedState({ attr, checked: !closed })}
        label="Closed"
        value={closed ? 'checked' : ''}
      />
    </CheckBoxWrapper>
  );

  return (
    <SingleDayContentWrapper data-aid={attr}>
      <DayLabel data-aid={`${attr}-dayLabel`}>{label}</DayLabel>
      <TimeRangesContainer>{renderedTimeRanges}</TimeRangesContainer>
      {closedCheckBox}
    </SingleDayContentWrapper>
  );
};

const OVERLAPPING_ERROR_REGEXP = /^(?<label>.+). The range index is: (?<rangeIndex>\d+)/;

const getOverlappingRangeErrorLabel = (
  attr: string,
  index: number,
  errors: { attr: string; code: string }[]
): string => {
  const overlapError = find(errors, error => error.attr === attr && error.code === 'hasNoOverlappingRanges');

  if (!overlapError) {
    return '';
  }

  const overlapErrorDetail = get(overlapError, 'detail', '');

  const { label = '', rangeIndex = -1 } = get(overlapErrorDetail.match(OVERLAPPING_ERROR_REGEXP), 'groups', {}) as any;

  return +rangeIndex === index ? label : '';
};

const ActionContainer = styled.div`
  align-self: flex-start;
  padding: 10px 12px 0 8px;
  min-width: 128px;
`;

const SingleInputRangeContainer = styled.div`
  align-items: center;
  display: flex;
  width: 100%;
`;

const StyledTrashIcon = styled(RemoveFillIcon)`
  visibility: hidden;
  fill: ${p => p.theme.colors.gray600};
  cursor: pointer;
  height: 16px;
  width: 16px;
`;

const TimeRangesContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const AddTimeRangeButton = styled(Button)`
  color: ${p => p.theme.colors.green400};
  padding: 0;

  &:disabled {
    color: ${p => p.theme.colors.gray400};
  }

  &:hover {
    color: ${p => p.theme.colors.gray600};
  }
`;

const DayLabel = styled.p`
  min-width: 85px;
  font-weight: 700;
`;

export const SingleDayContentWrapper = styled.div`
  display: flex;
  padding: 12px 16px;

  &:hover {
    background: ${p => p.theme.colors.green100};

    ${StyledTrashIcon} {
      visibility: visible;
    }
  }
`;

const CheckBoxWrapper = styled.div`
  margin-top: 0.8em;
  position: relative;
  padding-left: 10px;
`;
